import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const GlobalControls = ({ onTimeGranularityChange, onTenantFilterChange, onResourceToggle, onRefresh }) => {
  const [timeGranularity, setTimeGranularity] = useState('daily');
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [resourceTypes, setResourceTypes] = useState({
    storage: true,
    apiCalls: true,
    bandwidth: true,
    compute: true
  });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Auto-refresh every 10 minutes
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      setLastRefresh(new Date());
      onRefresh?.();
    }, 10 * 60 * 1000); // 10 minutes

    return () => clearInterval(interval);
  }, [autoRefresh, onRefresh]);

  const timeGranularityOptions = [
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' }
  ];

  const tenantOptions = [
    { value: 'all', label: 'All Tenants' },
    { value: 'enterprise', label: 'Enterprise Tier' },
    { value: 'professional', label: 'Professional Tier' },
    { value: 'starter', label: 'Starter Tier' },
    { value: 'trial', label: 'Trial Users' },
    { value: 'high-usage', label: 'High Usage' },
    { value: 'low-usage', label: 'Low Usage' }
  ];

  const handleTimeGranularityChange = (value) => {
    setTimeGranularity(value);
    onTimeGranularityChange?.(value);
  };

  const handleTenantFilterChange = (values) => {
    setSelectedTenants(values);
    onTenantFilterChange?.(values);
  };

  const handleResourceToggle = (resourceType) => {
    const newResourceTypes = {
      ...resourceTypes,
      [resourceType]: !resourceTypes[resourceType]
    };
    setResourceTypes(newResourceTypes);
    onResourceToggle?.(newResourceTypes);
  };

  const handleManualRefresh = () => {
    setLastRefresh(new Date());
    onRefresh?.();
  };

  const formatLastRefresh = (date) => {
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        {/* Left Section - Time Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
          <div className="flex items-center space-x-3">
            <Icon name="Clock" size={20} className="text-text-secondary" />
            <div className="space-y-1">
              <label className="text-sm font-medium text-text-primary">Time Granularity</label>
              <Select
                options={timeGranularityOptions}
                value={timeGranularity}
                onChange={handleTimeGranularityChange}
                className="w-32"
              />
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Icon name="Filter" size={20} className="text-text-secondary" />
            <div className="space-y-1">
              <label className="text-sm font-medium text-text-primary">Tenant Segments</label>
              <Select
                options={tenantOptions}
                value={selectedTenants}
                onChange={handleTenantFilterChange}
                multiple
                searchable
                placeholder="Select segments..."
                className="w-48"
              />
            </div>
          </div>
        </div>

        {/* Right Section - Resource Types & Refresh */}
        <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
          {/* Resource Type Toggles */}
          <div className="flex items-center space-x-3">
            <Icon name="Layers" size={20} className="text-text-secondary" />
            <div className="space-y-1">
              <label className="text-sm font-medium text-text-primary">Resource Types</label>
              <div className="flex items-center space-x-2">
                {Object.entries(resourceTypes).map(([type, enabled]) => (
                  <button
                    key={type}
                    onClick={() => handleResourceToggle(type)}
                    className={`
                      px-3 py-1 rounded-md text-xs font-medium transition-all duration-150 border
                      ${enabled 
                        ? 'bg-primary text-primary-foreground border-primary' 
                        : 'bg-muted text-text-secondary border-border hover:bg-muted/80'
                      }
                    `}
                  >
                    {type === 'apiCalls' ? 'API' : type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Refresh Controls */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`
                  flex items-center space-x-2 px-3 py-2 rounded-md text-sm transition-all duration-150
                  ${autoRefresh 
                    ? 'bg-success/10 text-success border border-success/20' :'bg-muted text-text-secondary border border-border hover:bg-muted/80'
                  }
                `}
              >
                <Icon name={autoRefresh ? 'Play' : 'Pause'} size={14} />
                <span>Auto-refresh</span>
              </button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleManualRefresh}
                iconName="RefreshCw"
              >
                Refresh
              </Button>
            </div>

            <div className="text-xs text-text-secondary">
              <div>Last updated:</div>
              <div className="font-medium">{formatLastRefresh(lastRefresh)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {(selectedTenants.length > 0 || Object.values(resourceTypes).some(v => !v)) && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-text-secondary">Active Filters:</span>
            
            {selectedTenants.length > 0 && (
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={14} className="text-text-secondary" />
                <div className="flex items-center space-x-1">
                  {selectedTenants.slice(0, 3).map((tenant) => (
                    <span key={tenant} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md">
                      {tenantOptions.find(opt => opt.value === tenant)?.label}
                    </span>
                  ))}
                  {selectedTenants.length > 3 && (
                    <span className="px-2 py-1 bg-muted text-text-secondary text-xs rounded-md">
                      +{selectedTenants.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {Object.values(resourceTypes).some(v => !v) && (
              <div className="flex items-center space-x-2">
                <Icon name="EyeOff" size={14} className="text-text-secondary" />
                <div className="flex items-center space-x-1">
                  {Object.entries(resourceTypes)
                    .filter(([, enabled]) => !enabled)
                    .map(([type]) => (
                      <span key={type} className="px-2 py-1 bg-error/10 text-error text-xs rounded-md">
                        {type} hidden
                      </span>
                    ))}
                </div>
              </div>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedTenants([]);
                setResourceTypes({ storage: true, apiCalls: true, bandwidth: true, compute: true });
                onTenantFilterChange?.([]);
                onResourceToggle?.({ storage: true, apiCalls: true, bandwidth: true, compute: true });
              }}
              className="text-xs text-text-secondary hover:text-text-primary"
            >
              Clear all
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalControls;